import { useTranslation } from "react-i18next";
import { ArrowRight, Check } from "lucide-react";
import leventFace from "@/assets/levent-face.jpg";
import { eclipseCopy } from "@/data/eclipseCopy";
import { BOOK, SQ, SLASH, ext, Mark, EclipseNav, EclipseFooter } from "@/components/EclipseChrome";

const sectionH = "m-0 max-w-[760px] text-[clamp(22px,2.2vw,30px)] font-medium tracking-[-0.02em] leading-[1.3]";
const wrap = "mx-auto max-w-[1200px] px-6";
const mono = "font-ec-mono text-[12px]";

const LimeCta = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <a href={BOOK} {...ext} className={`inline-flex items-center rounded-full bg-ec-lime px-6 py-3.5 text-[16px] font-medium text-ec-lime-ink no-underline hover:opacity-90 ${className}`}>
    {children}
  </a>
);

const EclipseLanding = () => {
  const { i18n } = useTranslation();
  const isDe = i18n.language?.startsWith("de");
  const c = eclipseCopy[isDe ? "de" : "en"];
  return (
    <div className="eclipse w-full overflow-hidden">
      {/* HERO */}
      <section className="ec-halo-hero relative">
        <div className={`${wrap} pb-[120px] pt-6`}>
          <EclipseNav />

          <div className="flex flex-wrap items-center gap-12 pt-[140px]">
            <div className="flex min-w-0 flex-col" style={{ flex: "999 1 560px" }}>
              <h1 className="m-0 max-w-[780px] text-[clamp(40px,5.4vw,72px)] font-medium leading-none tracking-[-0.04em]">{c.hero.h1}</h1>
              <div className="mt-7 max-w-[560px] text-[20px] font-medium leading-[1.4] tracking-[-0.01em]">
                {c.hero.lead1} <span className="text-ec-muted">{c.hero.lead2}</span>
              </div>
              <div className="mt-9"><LimeCta>{c.hero.cta}</LimeCta></div>
            </div>
            <div className="flex justify-end pr-2 lg:justify-center lg:pr-0" style={{ flex: "1 1 280px" }}>
              <svg width="260" height="260" viewBox="-20 -20 140 140" aria-hidden="true">
                <defs>
                  <filter id="heroEdge" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="0.8" />
                  </filter>
                </defs>
                <g transform="rotate(-14 50 50)">
                  <path d={SQ} fill="var(--ec-black)" filter="url(#heroEdge)" />
                  <path d={SQ} fill="var(--ec-black)" />
                  <g transform="translate(30 30) scale(0.4)"><path d={SLASH} fill="var(--ec-lime)" /></g>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUKTE: one panel, one number */}
      <section id="produkte" className={`${wrap} pt-32`}>
        <h2 className={sectionH}>{c.products.h}</h2>
        <a href="https://webblitz.ai" {...ext} className="mt-12 flex flex-wrap items-end justify-between gap-10 rounded-[28px] bg-ec-card p-8 text-ec-ink no-underline transition-opacity hover:opacity-95 md:p-12">
          <div className="flex min-w-0 flex-col gap-4" style={{ flex: "1 1 420px" }}>
            <div className="flex items-center gap-3">
              <span className="text-[clamp(28px,3vw,40px)] font-medium tracking-[-0.03em]">webblitz.ai ↗</span>
              <span className="rounded-full bg-ec-highlight px-2.5 py-1 text-[12px] font-semibold text-ec-lime-ink">Live</span>
            </div>
            <span className="max-w-[520px] text-[17px] leading-[1.55] text-ec-muted">{c.products.webblitz}</span>
          </div>
          <div className="flex flex-col items-start md:items-end">
            <span className="text-[clamp(72px,9vw,128px)] font-medium leading-none tracking-[-0.06em]">5</span>
            <span className={`${mono} mt-2 text-ec-muted`}>AI platforms · approve-gate</span>
          </div>
        </a>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-[20px] border border-ec-line px-7 py-5">
          <div className="flex items-center gap-3">
            <span className="select-none text-[18px] font-medium tracking-[-0.02em]">Project L.</span>
            <span className="rounded-full border border-ec-muted px-[9px] py-[3px] text-[12px] font-semibold text-ec-soft">Sneak peek</span>
          </div>
          <span className="max-w-[560px] text-[14px] leading-[1.5] text-ec-muted">{c.products.projectL}</span>
        </div>
      </section>

      {/* ZUSAMMENARBEIT */}
      <section id="zusammenarbeit" className={`${wrap} pt-32`}>
        <h2 className={sectionH}>{c.work.h}</h2>
        <div className="mt-12 flex flex-col gap-2.5 rounded-[28px] bg-ec-shell p-2.5">
          <div className="grid gap-2.5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(280px,100%), 1fr))" }}>
            {c.work.services.map((s) => (
              <div key={s.title} className="flex flex-col gap-3 rounded-[20px] bg-ec-shell-card p-[26px]">
                <span className="text-[20px] font-medium tracking-[-0.01em]">{s.title}</span>
                <span className="text-[15px] leading-[1.55] text-ec-muted">{s.body}</span>
                <div className="h-px bg-ec-line" />
                <span className="text-[14px] leading-[1.7] text-ec-soft">
                  {s.items.map((it) => <span key={it} className="block">+ {it}</span>)}
                </span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-7 px-[18px] pb-3.5 pt-[18px]">
            <div className="flex min-w-0 flex-col gap-1.5" style={{ flex: "1 1 260px" }}>
              <span className="text-[20px] font-medium tracking-[-0.01em]">{c.work.base.title}</span>
              <span className="text-[14px] leading-[1.55] text-ec-muted">{c.work.base.body}</span>
            </div>
            <div className="grid min-w-0 gap-4" style={{ flex: "2 1 520px", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
              {c.work.base.steps.map((t, i) => (
                <div key={t} className="flex flex-col gap-1">
                  <span className={`${mono} text-ec-muted`}>0{i + 1}</span>
                  <span className="text-[14px] leading-[1.4]">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4 grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {c.work.terms.map(([k, v]) => (
            <div key={k} className="flex flex-col gap-1 border-t border-ec-line pt-4">
              <span className={`${mono} text-ec-muted`}>{k}</span>
              <span className="text-[15px]">{v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* PRAXIS: a restrained editorial comparison */}
      <section id="praxis" aria-labelledby="practice-heading" className={`${wrap} pt-32`}>
        <h2 id="practice-heading" className="m-0 max-w-[760px] text-[26px] font-medium leading-[1.3] tracking-normal md:text-[30px]">{c.practice.h}</h2>
        <div className="mt-10 grid gap-8 border-y border-ec-line py-8 md:mt-12 md:grid-cols-[1fr_48px_1fr] md:gap-8 md:py-10">
          <div className="max-w-[440px]">
            <span className={`${mono} text-ec-muted`}>{c.practice.before}</span>
            <h3 className="mb-0 mt-4 text-[22px] font-medium leading-[1.35] tracking-normal text-ec-muted md:text-[24px]">{c.practice.beforeTitle}</h3>
            <p className="mb-0 mt-4 text-[16px] leading-[1.7] text-ec-muted">{c.practice.beforeBody}</p>
          </div>
          <ArrowRight aria-hidden="true" className="hidden h-5 w-5 self-center justify-self-center text-ec-dim md:block" />
          <div className="max-w-[440px] border-t border-ec-line pt-8 md:border-t-0 md:pt-0">
            <span className={`${mono} text-ec-soft`}>{c.practice.after}</span>
            <h3 className="mb-0 mt-4 text-[22px] font-medium leading-[1.35] tracking-normal md:text-[24px]">{c.practice.afterTitle}</h3>
            <p className="mb-0 mt-4 text-[16px] leading-[1.7] text-ec-muted">{c.practice.afterBody}</p>
          </div>
        </div>
      </section>

      {/* LOOP: fine connecting line, approval is the only filled marker */}
      <section id="loop" aria-labelledby="loop-heading" className={`${wrap} pt-32`}>
        <h2 id="loop-heading" className="m-0 max-w-[760px] text-[26px] font-medium leading-[1.3] tracking-normal md:text-[30px]">{c.loop.h}</h2>
        <ol className="m-0 mt-10 grid list-none gap-0 p-0 md:mt-14 md:grid-cols-6">
          {c.loop.steps.map(([n, t], i) => (
            <li key={n} className="relative grid grid-cols-[40px_1fr] gap-x-4 pb-7 last:pb-0 md:block md:pb-0 md:pr-5">
              {i < c.loop.steps.length - 1 && <span aria-hidden="true" className="absolute bottom-0 left-5 top-10 w-px bg-ec-line md:bottom-auto md:left-10 md:right-0 md:top-5 md:h-px md:w-auto" />}
              <span className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full ${i === 3 ? "bg-ec-highlight text-ec-lime-ink" : "border border-ec-line bg-ec-bg text-ec-muted"}`}>
                {i === 3 ? <Check aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} /> : <span className={mono}>0{i + 1}</span>}
              </span>
              <div className="min-w-0 pt-2 md:pt-6">
                <h3 className={`m-0 text-[17px] font-medium leading-6 tracking-normal ${i === 3 ? "text-ec-ink" : "text-ec-soft"}`}>{n}</h3>
                <p className="mb-0 mt-2 max-w-[340px] text-[14px] leading-[1.65] text-ec-muted">{t}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* OPERATOR */}
      <section id="operator" className={`${wrap} pt-32`}>
        <div className="flex flex-wrap items-center gap-14">
          <div className="relative h-[220px]" style={{ flex: "0 0 240px" }}>
            <Mark size={150} fill="var(--ec-mark)" className="absolute left-0 top-0 -rotate-[14deg]" />
            <a href="https://linkedin.com/in/levo" {...ext} aria-label="Levent on LinkedIn" className="absolute left-[82px] top-[62px] block h-[150px] w-[150px] rotate-[9deg]">
              <div className="flex h-[150px] w-[150px] items-center justify-center bg-ec-bg" style={{ clipPath: "path('M75 0C135 0 150 15 150 75C150 135 135 150 75 150C15 150 0 135 0 75C0 15 15 0 75 0Z')" }}>
                <img
                  src={leventFace}
                  alt="Levent Valente"
                  width={146}
                  height={146}
                  loading="lazy"
                  className="block h-[146px] w-[146px] object-cover"
                  style={{ clipPath: "path('M73 0C131.4 0 146 14.6 146 73C146 131.4 131.4 146 73 146C14.6 146 0 131.4 0 73C0 14.6 14.6 0 73 0Z')" }}
                />
              </div>
            </a>
          </div>
          <div className="flex min-w-0 flex-col" style={{ flex: "999 1 480px" }}>
            <span className={`${mono} text-ec-muted`}>{c.operator.h}</span>
            <p className="m-0 mt-4 max-w-[720px] text-[clamp(32px,4vw,52px)] font-medium leading-[1.05] tracking-[-0.04em]">
              {c.operator.rows[c.operator.rows.length - 1][1]}
            </p>
            <div className="mt-10 flex flex-col">
              {c.operator.rows.slice(0, -1).map(([k, v], i, arr) => (
                <div key={k} className={`flex flex-wrap gap-x-6 gap-y-2 border-t border-ec-line py-3.5 ${i === arr.length - 1 ? "border-b" : ""}`}>
                  <span className={`${mono} text-ec-muted`} style={{ flex: "0 0 120px" }}>{k}</span>
                  <span className="text-[15px]" style={{ flex: "1 1 300px" }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ec-halo-rise relative mt-32">
        <div className={`${wrap} flex flex-col items-center pb-40 pt-32 text-center`}>
          <h2 className="m-0 max-w-[820px] text-[clamp(32px,4.4vw,60px)] font-medium leading-[1.05] tracking-[-0.04em]">{c.final.h}</h2>
          <LimeCta className="mt-8 px-[26px]">{c.final.cta}</LimeCta>
          <span className={`${mono} mt-3.5 text-ec-halo-text`}>{c.final.note}</span>
        </div>
      </section>

      <EclipseFooter onHome />
    </div>
  );
};

export default EclipseLanding;
